// Users API on the shared apiClient (base URL, auth, error semantics).
// Replaces the user-management half of the retired services/api.ts ApiService;
// method signatures and return shapes are kept identical so call sites only
// swapped imports. `User`/`CreateUserData`/`UpdateUserData` live here — import
// them, never redeclare them.
import { deleteRequest, get, postJson, put } from './apiClient';

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  permissions?: Record<
    string,
    {
      view: boolean;
      create: boolean;
      edit: boolean;
      delete: boolean;
    }
  >;
}

export interface CreateUserData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  role: string;
  isActive: boolean;
}

export interface UpdateUserData {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isActive: boolean;
}

type Envelope<T> = { success: boolean; data: T };

// The API returns role as a populated object; the UI expects the role name as
// a plain string (the old ApiService did the same flattening).
function toClientUser(raw: any): User {
  return {
    ...raw,
    role: raw?.role?.name || raw?.role,
    createdAt: raw?.createdAt || new Date().toISOString(),
  };
}

export async function getUsers(): Promise<User[]> {
  const res = await get<Envelope<{ users: any[] }>>('/users');
  return (res.data.users || []).map(toClientUser);
}

/**
 * Create a user. On a 422-style refusal the thrown error carries
 * `isValidationError` + `validationErrors` (apiClient attaches them) so the
 * modal can mark the offending fields.
 */
export async function createUser(userData: CreateUserData): Promise<User> {
  const res = await postJson<Envelope<any>>('/users', userData);
  return toClientUser(res.data);
}

export async function updateUser(userId: string, userData: UpdateUserData): Promise<User> {
  const res = await put<Envelope<any>>(`/users/${userId}`, userData);
  return toClientUser(res.data);
}

export async function deleteUser(userId: string): Promise<void> {
  await deleteRequest(`/users/${userId}`);
}

export async function toggleUserStatus(userId: string, isActive: boolean): Promise<User> {
  const res = await put<Envelope<any>>(
    `/users/${userId}/${isActive ? 'activate' : 'deactivate'}`,
    {}
  );
  return toClientUser(res.data);
}
